import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { hours as hrs } from '../lib/hours';
import './HoursApprovalDetail.css';
import RequesterName from './RequesterName';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const day = (v) => (v ? String(v).slice(0, 10) : '—');
const when = (v) => (v ? String(v).replace('T', ' ').slice(0, 16) : '—');

/**
 * The ticket behind one approval request, before any work has been done.
 *
 * Sibling of HoursApprovalDetail and deliberately built on the same stylesheet,
 * because to an approver these are the same gesture at two points in a ticket's
 * life - open it, read what is actually there, decide. What differs is what
 * there is to read: no sessions yet, because nothing has been worked; instead
 * the conversation on the ticket, which is usually where the detail that
 * decides it has been written, and any earlier decision, which is what tells
 * you this is something that came back rather than something new.
 */
export default function TicketApprovalDetail({ ticket, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const closer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/approvals/${ticket.id}/detail`);
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not read this ticket');
    } finally {
      setLoading(false);
    }
  }, [ticket.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    closer.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const t = data?.ticket;

  return (
    <div
      className="had-scrim"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="had" role="dialog" aria-modal="true"
           aria-label={`${ticket.ticket_number}: awaiting approval`}>
        <div className="had-top">
          <div>
            <span className="had-num">{ticket.ticket_number}</span>
            <h2>{ticket.title}</h2>
          </div>
          <button type="button" className="had-x" onClick={onClose} ref={closer}
                  aria-label="Close">&times;</button>
        </div>

        {loading && <p className="had-quiet">Reading the ticket&hellip;</p>}
        {error && <p className="had-error" role="alert">{error}</p>}

        {data && (
          <>
            <dl className="had-facts">
              <div><dt>Raised by</dt><dd><RequesterName ticket={t} fallback="unknown" /></dd></div>
              <div><dt>Email</dt><dd>{t.requester_email || '—'}</dd></div>
              <div><dt>Status</dt><dd>{t.status || '—'}</dd></div>
              <div><dt>Priority</dt><dd>{t.priority || '—'}</dd></div>
              <div><dt>Category</dt><dd>{t.category || '—'}</dd></div>
              <div><dt>Department</dt><dd>{t.department || '—'}</dd></div>
              <div><dt>Opened</dt><dd>{day(t.created_at)}</dd></div>
              {t.allocated_hours > 0 && (
                <div><dt>Budget already set</dt><dd>{hrs(t.allocated_hours)}</dd></div>
              )}
              {t.contractor_name && (
                <div><dt>Contractor</dt><dd>
                  {t.contractor_name}{t.contractor_company ? ` (${t.contractor_company})` : ''}
                </dd></div>
              )}
              {t.approver_name && (
                <div><dt>Approver</dt><dd>{t.approver_name}</dd></div>
              )}
            </dl>

            <section className="had-sec">
              <h3>What was asked for</h3>
              {t.description
                ? <p className="had-desc">{t.description}</p>
                : <p className="had-quiet">
                    Nothing was written in the description. The comments below, if there
                    are any, are all there is to go on.
                  </p>}
            </section>

            <section className="had-sec">
              <h3>Comments<span className="had-count">{data.comments.length}</span></h3>
              {data.comments.length === 0
                ? <p className="had-quiet">Nothing has been written on this ticket.</p>
                : (
                  <ul className="had-feed">
                    {data.comments.map((c) => (
                      <li key={c.id}>
                        <span className="had-who">{c.by_contractor || c.by_user || 'unknown'}</span>
                        <span className="had-at">{when(c.created_at)}</span>
                        <p>{c.comment}</p>
                      </li>
                    ))}
                  </ul>
                )}
            </section>

            <section className="had-sec">
              <h3>History<span className="had-count">{data.history.length}</span></h3>
              {data.history.length === 0
                ? <p className="had-quiet">No recorded changes.</p>
                : (
                  <ul className="had-feed is-tight">
                    {data.history.map((h) => (
                      <li key={h.id}>
                        <span className="had-who">{h.by_contractor || h.by_user || 'system'}</span>
                        <span className="had-at">{when(h.created_at)}</span>
                        <p>
                          {h.status_to ? `${h.status_from || 'unset'} → ${h.status_to}` : 'noted'}
                          {h.reason ? ` — ${h.reason}` : ''}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
            </section>

            {/* No Accept here on purpose. Accepting needs a contractor chosen
                and a budget typed, both of which are on the card behind this;
                a second copy of that form would be two places to get it wrong. */}
            <div className="had-actions had-decide">
              <button type="button" className="had-btn is-primary" onClick={onClose}>
                Back to the decision
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
